<template>
  <div id="qsrComponentTest">

    <Panel id="parameters-container" title="Paramètres" collapsible>
      <div class="columns">

        <!-- Component selection -->
        <div class="column">
          <div class="group">
            <label>Component</label>
            <select v-model="components.selected">
              <option v-for="component of components.all" :value="component">{{component.name}}</option>
            </select>
          </div>
        </div>

        <!-- Context selection -->
        <div class="column">
          <div class="group" v-if="requiredParameters.has('company')">
            <label>Company</label>
            <AjaxSelect
              v-model="parameters.company"
              :api="`/api/entity/company/select`"
              :map-fn="(i) => `${i.branchName} - ${i.name}`"
              :allow-empty-search="true"
              :placeholder="$t('search')"
            />
          </div>
          <div class="group" v-if="requiredParameters.has('item')">
            <label>Item</label>
            <AjaxSelect
              v-model="parameters.item"
              :api="`/api/entity/item/select`"
              :map-fn="(i) => i.name"
              :allow-empty-search="true"
              :query="{inventory: inventoryId}"
              :disabled="!inventoryId"
              :placeholder="$t('search')"
            />
          </div>
          <div class="group" v-if="requiredParameters.has('employee')">
            <label>Employee</label>
            <AjaxSelect
              v-model="parameters.employee"
              :api="`/api/entity/employee/select`"
              :map-fn="(i) => `${i.firstName} ${i.lastName}`"
              :allow-empty-search="true"
              :query="{company: companyId}"
              :disabled="!companyId"
              :placeholder="$t('search')"
            />
          </div>
          <div class="group" v-if="requiredParameters.has('start')">
            <label>{{$t('start')}}</label>
            <datetime
              v-model="startProxy"
              type="datetime"
              value-zone="local"
              input-id="p_end"
              input-style="width: 100%"
              :max-datetime="endProxy"
            />
          </div>
          <div class="group" v-if="requiredParameters.has('end')">
            <label>{{$t('end')}}</label>
            <datetime
              v-model="endProxy"
              type="datetime"
              value-zone="local"
              input-id="p_end"
              input-style="width: 100%"
              :min-datetime="startProxy"
            />
          </div>
        </div>

      </div>

      <!-- Save -->
      <div style="display: flex; justify-content: space-between">
        <!-- Left -->
        <div v-for="action of saveActions">
          <button class="btn btn-primary" @click="save(action.entity, action.fields)">
            {{$t('save')}} {{action.entity}} ({{action.fields.join(', ')}})
          </button>
        </div>

        <!-- Right (Custom) -->
        <div>
          <button
            v-if="components.selected && components.selected.name === 'EmployeeForm' && !!company"
            class="btn btn-primary"
            @click="newEmployee">New Employee</button>
        </div>
      </div>
    </Panel>

    <Panel id="component-container" :title="components.selected.name" v-if="components.selected && areParametersReady">
      <!-- PriceSchemesQSR -->
      <template v-if="components.selected.name === 'PriceSchemesQSR'">
        <PriceSchemesQSR
          v-model="item.priceSchemes"
          :revenue-center-to-mode="revenueCenterToMode"
        />
      </template>

      <!-- EmployeeForm -->
      <template v-else-if="components.selected.name === 'EmployeeForm'">
        <EmployeeForm
          v-model="employee"
          :branch-id="branchId"
        />
      </template>

      <!-- CrossSells -->
      <template v-else-if="components.selected.name === 'CrossSells'">
        <CrossSells
          v-model="item.crossSells"
          :inventory-id="inventoryId"
          :item-id="item.id"
        />
      </template>

      <!-- TimesheetQSR -->
      <template v-else-if="components.selected.name === 'TimesheetQSR'">
        <TimesheetQSR
          v-model="timesheet"
          :employee="employee"
          :period-min-date="parameters.start"
          :period-max-date="parameters.end"
        />
      </template>

      <!-- PosRoles -->
      <template v-else-if="components.selected.name === 'PosRoles'">
        <ListMultiForm
          v-model="posRoles"
          :form-component="posRoleComponent"
          :form-props="{functions, permissionData: permissions}"
          :data-on-add="(i) => ({name: `role ${i}`})"
          title="ROLES"
          no-selection-text="--- Select role to modify ---"
        />
      </template>

      <!-- PosLayouts -->
      <template v-else-if="components.selected.name === 'PosLayouts'">
        <ListMultiForm
          v-model="posLayouts"
          :form-component="posLayoutComponent"
          :form-props="{functions}"
          :data-on-add="(i) => ({name: `layout ${i}`})"
          title="LAYOUTS"
          no-selection-text="--- Select a layout to modify ---"
        />
      </template>

      <!-- RevenueCenter -->
      <template v-else-if="components.selected.name === 'RevenueCenters'">
        <ListMultiForm
          v-model="revenueCenters"
          :form-component="revenueCenterComponent"
          :form-props="{functions, posModes}"
          :data-on-add="(i) => ({name: {en: `revenue center ${i}`}})"
          title="REVENUE CENTER"
          no-selection-text="--- Select a revenue center to modify ---"
        />
      </template>

      <!-- PosModes -->
      <template v-else-if="components.selected.name === 'PosModes'">
        <ListMultiForm
          v-model="posModes"
          :form-component="posModeComponent"
          :form-props="{functions}"
          :data-on-add="(i) => ({name: {en: `pos mode ${i}`}})"
          title="MODE"
          no-selection-text="--- Select a mode to modify ---"
        />
      </template>

      <!-- SHOW/HIDE data -->
      <div class="show-data">
        <div v-if="showData">
          <div v-if="employee && components.selected.name === 'EmployeeForm'">{{employee}}</div>
          <template v-if="item && item.priceSchemes && components.selected.name === 'PriceSchemesQSR'">
            <div v-for="scheme of item.priceSchemes" :key="`data-${scheme.method}-${scheme.mode}`">{{scheme}}</div>
          </template>
          <template v-if="item && item.crossSells && components.selected.name === 'CrossSells'">
            <div v-for="(cs, i) of item.crossSells" :key="`data-cs-${i}`">{{cs}}</div>
          </template>
          <template v-if="timesheet && components.selected.name === 'TimesheetQSR'">
            <div v-for="(e, i) of timesheet" :key="`data-entry-${i}`">{{e}}</div>
          </template>
          <template v-if="posRoles && components.selected.name === 'PosRoles'">
            <div v-for="(role, i) of posRoles" :key="`data-role-${i}`">{{role}}</div>
          </template>
          <template v-if="posLayouts && components.selected.name === 'PosLayouts'">
            <div v-for="(layout, i) of posLayouts" :key="`data-layout-${i}`">{{layout}}</div>
          </template>
          <template v-if="revenueCenters && components.selected.name === 'RevenueCenters'">
            <div v-for="(rc, i) of revenueCenters" :key="`data-rc-${i}`">{{rc}}</div>
          </template>
          <template v-if="posModes && components.selected.name === 'PosModes'">
            <div v-for="(mode, i) of posModes" :key="`data-mode-${i}`">{{mode}}</div>
          </template>
        </div>
        <div style="flex-grow: 1"></div>
        <div>
          <button class="btn btn-primary" @click="showData = !showData">{{showData ? 'HIDE' : 'SHOW'}} DATA</button>
        </div>
      </div>

    </Panel>
  </div>
</template>

<script>
import {Core} from "@/core/Core";
import AjaxSelect from "@/components/tools/AjaxSelect";
import CrossSells from "@/components/qsr/CrossSells";
import PriceSchemesQSR from "@/components/qsr/PriceSchemesQSR";
import EmployeeForm from "@/components/qsr/EmployeeForm";
import TimesheetQSR from "@/components/qsr/TimesheetEmployee";
import ListMultiForm from "@/components/qsr/ListMultiForm";
import PosRole from "@/components/qsr/PosRole";
import PosLayout from "@/components/qsr/PosLayout";
import RevenueCenter from "@/components/qsr/RevenueCenter";
import PosMode from "@/components/qsr/PosMode";
import {DateTime} from "luxon";

export default {
  name: "ComponentTestQSR",
  components: {
    AjaxSelect, ListMultiForm,
    CrossSells, PriceSchemesQSR, TimesheetQSR,
    EmployeeForm, PosRole, PosLayout, RevenueCenter, PosMode
  },

  data() {
    return {
      components: {
        selected: null,
        all: [
          {
            name: 'PriceSchemesQSR',
            required: ['item', 'revenueCenters'],
            save: {
              item: ['priceSchemes']
            }
          }, {
            name: 'CrossSells',
            required: ['item'],
            save: {
              item: ['crossSells']
            }
          }, {
            name: 'EmployeeForm',
            required: ['employee'],
            save: {
              employee: ['all']
            }
          }, {
            name: 'TimesheetQSR',
            required: ['timesheet']
          }, {
            name: 'PosRoles',
            required: ['company', 'posRoles', 'functions', 'permissions'],
            save: {
              posRoles: ['all']
            }
          }, {
            name: 'PosLayouts',
            required: ['company', 'posLayouts', 'functions'],
            save: {
              posLayouts: ['all']
            }
          }, {
            name: 'RevenueCenters',
            required: ['company', 'revenueCenters', 'functions', 'posModes'],
            save: {
              revenueCenters: ['all']
            }
          }, {
            name: 'PosModes',
            required: ['company', 'posModes', 'functions'],
            save: {
              posModes: ['all']
            }
          }
        ]
      },
      parameters: {
        company: {id: null},
        employee: {id: null},
        item: {id: null},
        start: '2022-03-01 00:00:00',
        end: '2022-03-31 23:59:59'
      },

      /* in context */
      employee: null,
      item: null,
      company: null,
      timesheet: null,
      posRoles: null,
      posLayouts: null,
      revenueCenters: null,
      posModes: null,
      functions: null,
      permissions: null,

      /* */
      showData: false
    }
  },

  computed: {
    posRoleComponent() {
      return PosRole;
    },
    posLayoutComponent() {
      return PosLayout;
    },
    revenueCenterComponent() {
      return RevenueCenter;
    },
    posModeComponent() {
      return PosMode;
    },
    revenueCenterToMode() {
      let arr = [];
      for (let rc of this.revenueCenters) {
        for (let mode of rc.modes) {
          arr.push({
            mode:   {id: mode.id, name: mode.name},
            center: {id: rc.id,   name: rc.name},
            tag: mode.context.tag
          })
        }
      }
      return arr;
    },
    startProxy: {
      get() { return this.toIsoTime(this.parameters.start) },
      set(value) { this.parameters.start = this.fromIsoTime(value) }
    },
    endProxy: {
      get() { return this.toIsoTime(this.parameters.end) },
      set(value) { this.parameters.end = this.fromIsoTime(value) }
    },
    saveActions() {
      return this.components.selected && this.components.selected.save
        ? Object.entries(this.components.selected.save).map(([entity, fields]) => ({entity, fields}))
        : []
    },
    requiredParameters() {
      if (!this.components.selected) {
        return new Set();
      }
      let required = new Set(this.components.selected.required || []);
      if (required.has('timesheet')) {
        required.add('start');
        required.add('end');
        required.add('employee');
      }
      if (required.has('item')) {
        required.add('company');
      }
      if (required.has('employee')) {
        required.add('company');
      }
      return required;
    },
    areParametersReady() {
      return Array.from(this.requiredParameters).every(p => this[p] === undefined || !!this[p]);
    },
    inventoryId() {
      return this.company ? this.company.inventory.id : null;
    },
    companyId() {
      return this.company ? this.company.id : null;
    },
    branchId() {
      return this.company ? this.company.branch : null;
    },
    timesheetWatched() {
      let watched = this.components.selected
          && this.components.selected.name === 'TimesheetQSR'
          && !!this.parameters.employee.id
          && !!this.parameters.company.id
          && !!this.parameters.start
          && !!this.parameters.end;

      return !watched ? null : {
        company: this.parameters.company.id,
        employee: this.parameters.employee.id,
        start: this.parameters.start,
        end: this.parameters.end
      }
    },
    posRolesWatched() {
      return this.requiredParameters.has('posRoles') && !!this.parameters.company.id
        ? {company: this.parameters.company.id}
        : null;
    },
    posLayoutsWatched() {
      return this.requiredParameters.has('posLayouts') && !!this.parameters.company.id
        ? {company: this.parameters.company.id}
        : null;
    },
    revenueCentersWatched() {
      return this.requiredParameters.has('revenueCenters') && !!this.parameters.company.id
        ? {company: this.parameters.company.id}
        : null;
    },
    posModesWatched() {
      return this.requiredParameters.has('posModes') && !!this.parameters.company.id
        ? {company: this.parameters.company.id}
        : null;
    },
    functionsWatched() {
      return this.requiredParameters.has('functions');
    },
    permissionsWatched() {
      return this.requiredParameters.has('permissions');
    }
  },

  mounted() {
    this.components.selected = this.components.all[0];
  },

  watch: {
    "parameters.company": {
      deep: true,
      async handler(val) {
        this.company = val.id ? await Core.request('GET', `/api/branches/${val.branch}/companies/${val.id}?augment=backend`) : null;
      }
    },
    "parameters.employee": {
      deep: true,
      async handler(val) {
        this.employee = val.id ? await Core.request('GET', `/api/employees/${val.id}?augment=embed`) : null;
      }
    },
    "parameters.item": {
      deep: true,
      async handler(val) {
        this.item = val.id ? await Core.request('GET', `/api/inventory/${this.inventoryId}/items/${val.id}`) : null;
      }
    },
    timesheetWatched: {
      deep: true,
      async handler(val) {
        if (!val) {
          this.timesheet = null;
          return;
        }
        val.perPage = -1
        let response = await Core.request('GET', '/api/timesheet_entries', val);
        this.timesheet = response.data;
      }
    },
    posRolesWatched: {
      async handler(val) {
        if (!val) {
          this.posRoles = null;
          return;
        }
        let response = await Core.request('GET', `/api/companies/${val.company}/pos_roles`);
        this.posRoles = response.posRoles;
      }
    },
    posLayoutsWatched: {
      async handler(val) {
        if (!val) {
          this.posLayouts = null;
          return;
        }
        let response = await Core.request('GET', `/api/companies/${val.company}/pos_layouts`);
        this.posLayouts = response.posLayouts;
      }
    },
    revenueCentersWatched: {
      async handler(val) {
        if (!val) {
          this.revenueCenters = null;
          return;
        }
        let response = await Core.request('GET', `/api/companies/${val.company}/revenue_centers`);
        this.revenueCenters = response.revenueCenters;
      }
    },
    posModesWatched: {
      async handler(val) {
        if (!val) {
          this.posModes = null;
          return;
        }
        let response = await Core.request('GET', `/api/companies/${val.company}/pos_modes`);
        this.posModes = response.posModes;
      }
    },
    functionsWatched: {
      async handler(functionsRequired) {
        if (!functionsRequired || !!this.functions) {
          return;
        }
        let response = await Core.request('GET', '/api/pos_util/functions');
        this.functions = response.functions;
      }
    },
    permissionsWatched: {
      async handler(permissionsRequired) {
        if (!permissionsRequired || !!this.permissions) {
          return;
        }
        this.permissions = await Core.request('GET', '/api/pos_util/permissions');
      }
    }
  },

  methods: {
    async save(entity, fields) {
      if (entity === 'company') {
        let data = this.filterObjectByFields(this.company, fields);
        this.company = await Core.request('PUT', `/api/branches/${this.company.branch}/companies/${this.company.id}?augment=backend`, data);
        toast('Company saved', 'success');

      } else if (entity === 'employee') {
        let data = this.filterObjectByFields(this.employee, fields);
        this.employee = this.employee.id
          ? await Core.request('PUT',  `/api/employees/${this.employee.id}?augment=embed`, data)
          : await Core.request('POST', `/api/employees?augment=embed`, data);

        toast('Employee saved', 'success');

      } else if (entity === 'item') {
        let data = this.filterObjectByFields(this.item, fields);
        this.item = await Core.request('PUT', `/api/inventory/${this.inventoryId}/items/${this.item.id}`, data);
        toast('Item saved', 'success');

      } else if (entity === 'posRoles') {
        let response = await Core.request('POST', `/api/companies/${this.company.id}/pos_roles`, {posRoles: this.posRoles});
        this.posRoles = response.posRoles;
        toast('PosRoles saved', 'success');

      } else if (entity === 'posLayouts') {
        let response = await Core.request('POST', `/api/companies/${this.company.id}/pos_layouts`, {posLayouts: this.posLayouts});
        this.posLayouts = response.posLayouts;
        toast('PosLayouts saved', 'success');

      } else if (entity === 'revenueCenters') {
        let response = await Core.request('POST', `/api/companies/${this.company.id}/revenue_centers`, {revenueCenters: this.revenueCenters});
        this.revenueCenters = response.revenueCenters;
        toast('RevenueCenters saved', 'success');

      } else if (entity === 'posModes') {
        let response = await Core.request('POST', `/api/companies/${this.company.id}/pos_modes`, {posModes: this.posModes});
        this.posModes = response.posModes;
        toast('PosModes saved', 'success');
      }
    },
    filterObjectByFields(obj, fields) {
      return fields.includes('all')
        ? JSON.parse(JSON.stringify(obj))
        : this.filterObjectByKeys(obj, fields);
    },
    filterObjectByKeys(obj, keys) {
      return Object.fromEntries(
        Object.entries(obj).filter(([k,_]) => keys.includes(k))
      );
    },
    fromIsoTime(isoTime) {
      return isoTime ? DateTime.fromISO(isoTime).toFormat('yyyy-MM-dd HH:mm:ss') : null
    },
    toIsoTime(crudeTime) {
      return crudeTime ? DateTime.fromFormat(crudeTime, 'yyyy-MM-dd HH:mm:ss').toISO() : null
    },
    newEmployee() {
      this.employee = {
        id: null,
        companies: [],
        branch: this.company.branch,
        preferences: {}
      };
    }
  }
}
</script>

<style lang="scss" scoped>
#parameters-container {
  margin-bottom: 20px;
}
.show-data {
  display: flex;
  margin-top: 30px;
  button {
    white-space: nowrap;
  }
}
</style>